import { useState, useEffect, useCallback } from "react";
import { Loader2, AlertCircle, Plus, Pencil, Trash2, BookOpen, DollarSign, TrendingUp, Target } from "lucide-react";
import { colors, fonts, BOOK_SALE_STAGES } from "../styles/theme";
import { useTenant } from "../contexts/TenantContext";
import { Badge, Button, SearchBar, StatCard } from "./ui";
import { BookSaleModal } from "./BookSaleModal";
import {
  fetchBookOpportunities,
  deleteBookOpportunity,
  updateBookOpportunity,
  fmtDate,
} from "../services/api";

const stageMap = {};
BOOK_SALE_STAGES.forEach((s) => {
  stageMap[s.id] = s;
});

export default function BookSales() {
  const { formatCurrency } = useTenant();
  const [opportunities, setOpportunities] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingOpp, setEditingOpp] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchBookOpportunities({
        search: search || undefined,
        status: statusFilter || undefined,
      });
      setOpportunities(data.opportunities || []);
      setTotal(data.total || 0);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = async (opp) => {
    if (!window.confirm(`Delete "${opp.companyName}" opportunity?`)) return;
    try {
      await deleteBookOpportunity(opp.id);
      loadData();
    } catch (err) {
      alert("Delete failed: " + err.message);
    }
  };

  const handleStatusChange = async (opp, newStatus) => {
    try {
      await updateBookOpportunity(opp.id, { status: newStatus });
      setOpportunities((prev) =>
        prev.map((o) => (o.id === opp.id ? { ...o, status: newStatus } : o)),
      );
    } catch (err) {
      alert("Status update failed: " + err.message);
    }
  };

  const handleSaved = () => {
    setShowModal(false);
    setEditingOpp(null);
    loadData();
  };

  // Summary stats
  const activeOpps = opportunities.filter(
    (o) => !["won", "lost"].includes(o.status),
  );
  const pipelineValue = opportunities
    .filter((o) => o.status !== "lost")
    .reduce((sum, o) => sum + (o.estimatedValue || 0), 0);
  const wonValue = opportunities
    .filter((o) => o.status === "won")
    .reduce((sum, o) => sum + (o.estimatedValue || 0), 0);
  const weightedPipeline = opportunities
    .filter((o) => !["won", "lost"].includes(o.status))
    .reduce((sum, o) => {
      const prob = o.probability != null ? o.probability : (stageMap[o.status]?.defaultProbability ?? 0);
      return sum + Math.round((o.estimatedValue || 0) * prob / 100);
    }, 0);

  if (loading && opportunities.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "40vh",
        }}
      >
        <Loader2
          size={24}
          color={colors.accent}
          className="spin"
        />      </div>
    );
  }

  if (error && opportunities.length === 0) {
    return (
      <div style={{ padding: 32, textAlign: "center", color: colors.danger }}>
        <AlertCircle size={24} style={{ marginBottom: 8 }} />
        <div>{error}</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 24,
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: fonts.heading,
              fontSize: 28,
              fontWeight: 700,
              color: colors.text,
              marginBottom: 4,
            }}
          >
            Book Sales
          </h1>
          <p
            style={{
              fontFamily: fonts.body,
              fontSize: 14,
              color: colors.textSecondary,
            }}
          >
            {activeOpps.length} active opportunities · {total} total
          </p>
        </div>
        <Button
          variant="primary"
          icon={Plus}
          onClick={() => {
            setEditingOpp(null);
            setShowModal(true);
          }}
        >
          Add Opportunity
        </Button>
      </div>

      {/* Summary Stats */}
      <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
        <StatCard
          icon={BookOpen}
          label="Active Opportunities"
          value={activeOpps.length}
        />
        <StatCard
          icon={DollarSign}
          label="Pipeline Value"
          value={formatCurrency(pipelineValue)}
        />
        <StatCard
          icon={Target}
          label="Weighted Pipeline"
          value={formatCurrency(weightedPipeline)}
        />
        <StatCard
          icon={TrendingUp}
          label="Won Value"
          value={formatCurrency(wonValue)}
        />
      </div>

      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search companies..."
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: "8px 12px",
            fontSize: 13,
            fontFamily: fonts.body,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            background: colors.bgCard,
            color: colors.text,
            outline: "none",
            cursor: "pointer",
          }}
        >
          <option value="">All Stages</option>
          {BOOK_SALE_STAGES.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      {opportunities.length === 0 ? (
        <div
          style={{
            padding: 48,
            textAlign: "center",
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
          }}
        >
          <BookOpen
            size={32}
            color={colors.textSecondary}
            style={{ marginBottom: 12 }}
          />
          <div
            style={{
              fontSize: 14,
              color: colors.textSecondary,
              marginBottom: 8,
            }}
          >
            No book sale opportunities yet
          </div>
          <Button
            variant="primary"
            size="sm"
            icon={Plus}
            onClick={() => {
              setEditingOpp(null);
              setShowModal(true);
            }}
          >
            Add your first opportunity
          </Button>
        </div>
      ) : (
        <div
          style={{
            overflowX: "auto",
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontFamily: fonts.body,
              fontSize: 13,
            }}
          >
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                {["Company", "Contact", "Status", "Qty", "Value", "Prob", "Weighted", "Updated", ""].map(
                  (label) => (
                    <th
                      key={label || "actions"}
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        color: colors.textSecondary,
                        fontWeight: 600,
                        fontSize: 11,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        background: colors.bgCard,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {label}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {opportunities.map((opp, i) => {
                const stage = stageMap[opp.status] || {
                  label: opp.status,
                  color: "#94a3b8",
                };
                return (
                  <tr
                    key={opp.id}
                    style={{
                      borderBottom: `1px solid ${colors.border}`,
                      background:
                        i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.02)",
                      transition: "background 0.1s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = colors.bgHover)
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background =
                        i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.02)")
                    }
                  >
                    <td
                      style={{
                        padding: "12px 16px",
                        color: colors.text,
                        fontWeight: 600,
                      }}
                    >
                      {opp.companyName}
                      {opp.notes && (
                        <div
                          style={{
                            fontSize: 11,
                            color: colors.textSecondary,
                            fontWeight: 400,
                            marginTop: 2,
                            maxWidth: 240,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {opp.notes}
                        </div>
                      )}
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        color: colors.textSecondary,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {opp.contactPerson || "—"}
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <select
                        value={opp.status}
                        onChange={(e) =>
                          handleStatusChange(opp, e.target.value)
                        }
                        style={{
                          padding: "3px 8px",
                          borderRadius: 4,
                          fontSize: 11,
                          fontWeight: 600,
                          fontFamily: fonts.body,
                          border: `1px solid ${stage.color}40`,
                          background: stage.color + "1a",
                          color: stage.color,
                          cursor: "pointer",
                          outline: "none",
                        }}
                      >
                        {BOOK_SALE_STAGES.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        color: colors.text,
                        fontFamily: fonts.mono,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {opp.estimatedQuantity || "—"}
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        color: colors.text,
                        fontFamily: fonts.mono,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {opp.estimatedValue
                        ? formatCurrency(opp.estimatedValue)
                        : "—"}
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        fontFamily: fonts.mono,
                        whiteSpace: "nowrap",
                        color: opp.probability != null ? colors.accent : colors.textSecondary,
                        fontSize: 12,
                      }}
                    >
                      {(() => {
                        const prob = opp.probability != null ? opp.probability : stage.defaultProbability;
                        return `${prob}%`;
                      })()}
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        fontFamily: fonts.mono,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        color: colors.textSecondary,
                        fontSize: 12,
                      }}
                    >
                      {(() => {
                        if (!opp.estimatedValue) return "—";
                        const prob = opp.probability != null ? opp.probability : stage.defaultProbability;
                        return formatCurrency(Math.round(opp.estimatedValue * prob / 100));
                      })()}
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        color: colors.textSecondary,
                        fontSize: 12,
                        fontFamily: fonts.mono,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fmtDate(opp.updatedAt)}
                    </td>
                    <td style={{ padding: "12px 8px", whiteSpace: "nowrap" }}>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button
                          onClick={() => {
                            setEditingOpp(opp);
                            setShowModal(true);
                          }}
                          title="Edit"
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: colors.textSecondary,
                            padding: 4,
                          }}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(opp)}
                          title="Delete"
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: colors.danger,
                            padding: 4,
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Stage Summary */}
      <div
        style={{
          marginTop: 20,
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        {BOOK_SALE_STAGES.map((stg) => {
          const stgOpps = opportunities.filter((o) => o.status === stg.id);
          const stgValue = stgOpps.reduce((s, o) => s + (o.estimatedValue || 0), 0);
          return (
            <div
              key={stg.id}
              onClick={() =>
                setStatusFilter(statusFilter === stg.id ? "" : stg.id)
              }
              style={{
                flex: 1,
                minWidth: 100,
                padding: "16px 20px",
                background: colors.bgCard,
                border: `1px solid ${statusFilter === stg.id ? stg.color : colors.border}`,
                borderRadius: 8,
                textAlign: "center",
                cursor: "pointer",
                transition: "border-color 0.15s",
              }}
            >
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  fontFamily: fonts.heading,
                  color: stg.color,
                }}
              >
                {stgOpps.length}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: colors.textSecondary,
                  fontFamily: fonts.body,
                }}
              >
                {stg.label}
              </div>
              {stgValue > 0 && (
                <div style={{ fontSize: 11, fontFamily: fonts.mono, color: colors.textSecondary, marginTop: 4 }}>
                  {formatCurrency(stgValue)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {showModal && (
        <BookSaleModal
          opportunity={editingOpp}
          onClose={() => {
            setShowModal(false);
            setEditingOpp(null);
          }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
